import { MutableRefObject, useEffect, useRef, useState } from 'react';

import {
  FeedbackEntry,
  FeedbackModel,
  useFeedbackModel,
} from '../model/Feedback.model';

export const CHANNEL = 'exercise-catalog-prototype';

type FeedbackState = {
  status: 'loading' | 'ready' | 'error' | 'hidden';
  latest: FeedbackEntry | null;
};

type HostMessage =
  | ({ type: 'feedback:state' } & FeedbackState)
  | { type: 'feedback:sent'; latest: FeedbackEntry }
  | { type: 'feedback:failed' };

type SubmittedAnswers = {
  rating: number;
  note: string;
  [question: string]: string | number;
};

type FrameMessage =
  | { type: 'ready' }
  | { type: 'feedback:submit'; answers: SubmittedAnswers };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const textAnswers = (answers: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(answers).filter(([, value]) => typeof value === 'string'),
  );

const toFrameMessage = (data: unknown): FrameMessage | null => {
  if (!isRecord(data) || data.channel !== CHANNEL) return null;
  if (data.type === 'ready') return { type: 'ready' };
  const answers = data.answers;
  if (
    data.type !== 'feedback:submit' ||
    !isRecord(answers) ||
    typeof answers.rating !== 'number'
  ) {
    return null;
  }
  return {
    type: 'feedback:submit',
    answers: {
      ...textAnswers(answers),
      rating: answers.rating,
      note: typeof answers.note === 'string' ? answers.note : '',
    },
  };
};

const toFeedbackState = (model: FeedbackModel): FeedbackState => {
  if (!model.canSendFeedback) return { status: 'hidden', latest: null };
  switch (model.latest.type) {
    case 'fetched':
      return { status: 'ready', latest: model.latest.value };
    case 'fetch-error':
      return { status: 'error', latest: null };
    default:
      return { status: 'loading', latest: null };
  }
};

const postToFrame = (frame: HTMLIFrameElement | null, message: HostMessage) =>
  frame?.contentWindow?.postMessage(
    { channel: CHANNEL, ...message },
    window.location.origin,
  );

const toPrototypeUrl = (creatorId: number) => {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  return `${base}/prototypes/exercise-catalog/index.html?embed=1&creatorId=${creatorId}`;
};

type PrototypeHostVMParams = { creatorId: number };

type PrototypeHostVM = {
  frameRef: MutableRefObject<HTMLIFrameElement | null>;
  frameSrc: string;
};

export const usePrototypeHostViewModel = (
  p: PrototypeHostVMParams,
): PrototypeHostVM => {
  const model = useFeedbackModel();
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const isSendingRef = useRef(false);
  const [readyCount, setReadyCount] = useState(0);
  const { status, latest } = toFeedbackState(model);

  useEffect(() => {
    if (readyCount === 0) return;
    postToFrame(frameRef.current, { type: 'feedback:state', status, latest });
  }, [readyCount, status, latest]);

  const send = (answers: SubmittedAnswers) => {
    if (!model.canSendFeedback || isSendingRef.current) return;
    isSendingRef.current = true;
    model
      .dispatch('Send', answers)
      .then(
        (sent) =>
          postToFrame(frameRef.current, {
            type: 'feedback:sent',
            latest: sent,
          }),
        () => postToFrame(frameRef.current, { type: 'feedback:failed' }),
      )
      .finally(() => {
        isSendingRef.current = false;
      });
  };

  const sendRef = useRef(send);
  sendRef.current = send;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const frameWindow = frameRef.current?.contentWindow;
      if (
        event.origin !== window.location.origin ||
        !frameWindow ||
        event.source !== frameWindow
      ) {
        return;
      }
      const message = toFrameMessage(event.data);
      if (message?.type === 'ready') setReadyCount((count) => count + 1);
      if (message?.type === 'feedback:submit') sendRef.current(message.answers);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return { frameRef, frameSrc: toPrototypeUrl(p.creatorId) };
};
